import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  getAdminAccountDetails,
  updateAdminAccountRequest,
} from "../../../services/api/account/accountApi";

const rejectApiResponse = (responseData, fallbackMessage) => {
  if (responseData?.success === false) {
    return responseData.message ?? fallbackMessage;
  }
  return null;
};

const ADMIN_ACCOUNT_UPDATE_ERROR_FALLBACK = {
  en: "Failed to update admin account",
  ar: "فشل في تحديث الحساب",
};

export const fetchAdminAccountDetails = createAsyncThunk(
  "adminAccount/fetchAdminAccountDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAdminAccountDetails();
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch admin account details");
    }
  },
);

export const updateAdminAccount = createAsyncThunk(
  "adminAccount/updateAdminAccount",
  async (
    { email, password, password_confirmation, otp_enabled, phone_number },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateAdminAccountRequest({
        email,
        password,
        password_confirmation,
        otp_enabled,
        phone_number,
      });

      const apiError = rejectApiResponse(
        response.data,
        ADMIN_ACCOUNT_UPDATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        accountDetails: response.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, ADMIN_ACCOUNT_UPDATE_ERROR_FALLBACK),
      );
    }
  },
);

const adminAccountSlice = createSlice({
  name: "adminAccount",
  initialState: {
    accountDetails: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminAccountDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminAccountDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.accountDetails = action.payload;
      })
      .addCase(fetchAdminAccountDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAdminAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminAccount.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.accountDetails) {
          state.accountDetails = action.payload.accountDetails;
        }
      })
      .addCase(updateAdminAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default adminAccountSlice.reducer;
